import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Button,
  IconButton,
  Tooltip,
  Collapse,
  useTheme,
  Divider,
  Grid,
  TextField
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  ContentCopy as CopyIcon,
  Check as CheckIcon,
  Block as BlockIcon
} from '@mui/icons-material';
import { CompareDataItem } from '../types/ImportExportTypes';
import { 
  deepCompareValues, 
  compareFieldValues, 
  compareRecordsDetailed, 
  createUnifiedFieldOrder,
  convertValueToType,
  getTypeSafeComparisonResult
} from '../utils';

interface CompareDataTableProps {
  data: CompareDataItem[];
  onAcceptChanges?: () => void;
  onRejectChanges?: () => void;
  onDataChange?: (index: number, field: string, value: any) => void;
  showOnlyConflicts?: boolean;
  isInAIAvatarLayout?: boolean;
}

const CompareDataTable: React.FC<CompareDataTableProps> = ({
  data,
  onAcceptChanges,
  onRejectChanges,
  onDataChange,
  showOnlyConflicts = false,
  isInAIAvatarLayout = false
}) => {
  const theme = useTheme();
  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());
  const [editableData, setEditableData] = useState<CompareDataItem[]>(data);
  const [copiedFields, setCopiedFields] = useState<Set<string>>(new Set());
  const [ignoredFields, setIgnoredFields] = useState<Set<string>>(new Set());

  // Update editable data when props change
  useEffect(() => {
    setEditableData(data);
  }, [data]);

  const handleFieldEdit = (index: number, field: string, value: any) => {
    setEditableData(prev => {
      const newData = [...prev];
      if (newData[index] && newData[index].incoming) {
        newData[index] = {
          ...newData[index],
          incoming: {
            ...newData[index].incoming,
            [field]: value
          }
        };
      }
      return newData;
    });
    
    // Call parent callback if provided
    onDataChange?.(index, field, value);
  };

  const handleCopyField = (index: number, field: string) => {
    const currentValue = editableData[index]?.current?.[field];
    if (currentValue !== undefined) {
      // Get the original incoming value to preserve its type
      const originalIncomingValue = editableData[index]?.incoming?.[field];
      
      setEditableData(prev => {
        const newData = [...prev];
        if (newData[index]) {
          // Ensure incoming object exists
          if (!newData[index].incoming) {
            newData[index].incoming = {};
          }
          
          // Convert current value to match the type of incoming value if possible
          let convertedValue = currentValue;
          if (originalIncomingValue !== undefined) {
            const targetType = typeof originalIncomingValue;
            convertedValue = convertValueToType(currentValue, targetType);
          }
          
          newData[index] = {
            ...newData[index],
            incoming: {
              ...newData[index].incoming,
              [field]: convertedValue
            }
          };
        }
        return newData;
      });
      
      // Show copied feedback
      setCopiedFields(prev => {
        const newSet = new Set(prev);
        newSet.add(`${index}-${field}`);
        return newSet;
      });
      setTimeout(() => {
        setCopiedFields(prev => {
          const newSet = new Set(prev);
          newSet.delete(`${index}-${field}`);
          return newSet;
        });
      }, 2000);
      
      // Call parent callback with the converted value
      const convertedValue = originalIncomingValue !== undefined ? 
        convertValueToType(currentValue, typeof originalIncomingValue) : currentValue;
      
      onDataChange?.(index, field, convertedValue);
    }
  };

  const handleIgnoreField = (index: number, field: string) => {
    const currentValue = editableData[index]?.current?.[field];
    if (currentValue !== undefined) {
      // Get the original incoming value to preserve its type
      const originalIncomingValue = editableData[index]?.incoming?.[field];
      
      setEditableData(prev => {
        const newData = [...prev];
        if (newData[index]) {
          // Ensure incoming object exists
          if (!newData[index].incoming) {
            newData[index].incoming = {};
          }
          
          // Convert current value to match the type of incoming value if possible
          let convertedValue = currentValue;
          if (originalIncomingValue !== undefined) {
            const targetType = typeof originalIncomingValue;
            convertedValue = convertValueToType(currentValue, targetType);
          }
          
          newData[index] = {
            ...newData[index],
            incoming: {
              ...newData[index].incoming,
              [field]: convertedValue
            }
          };
        }
        return newData;
      });
      
      // Mark field as ignored
      setIgnoredFields(prev => {
        const newSet = new Set(prev);
        newSet.add(`${index}-${field}`);
        return newSet;
      });
      
      // Call parent callback with the converted value
      const convertedValue = originalIncomingValue !== undefined ? 
        convertValueToType(currentValue, typeof originalIncomingValue) : currentValue;
      
      onDataChange?.(index, field, convertedValue);
    }
  };


  const isFieldIdentical = (index: number, field: string) => {
    // If field is ignored, it's considered identical (using current value)
    if (isFieldIgnored(index, field)) {
      return true;
    }
    
    const currentValue = editableData[index]?.current?.[field];
    const incomingValue = editableData[index]?.incoming?.[field];
    return deepCompareValues(currentValue, incomingValue);
  };

  const isFieldDifferent = (index: number, field: string) => {
    // If field is ignored, it's not different (using current value)
    if (isFieldIgnored(index, field)) {
      return false;
    }
    
    const currentValue = editableData[index]?.current?.[field];
    const incomingValue = editableData[index]?.incoming?.[field];
    return currentValue !== undefined && incomingValue !== undefined && 
           !deepCompareValues(currentValue, incomingValue);
  };

  const isFieldMissingInIncoming = (index: number, field: string) => {
    const currentValue = editableData[index]?.current?.[field];
    const incomingValue = editableData[index]?.incoming?.[field];
    return currentValue !== undefined && (incomingValue === undefined || incomingValue === null);
  };

  const isFieldIgnored = (index: number, field: string) => {
    return ignoredFields.has(`${index}-${field}`);
  };

  const resetIgnoredFields = () => {
    setIgnoredFields(new Set());
  };

  const getIgnoredFieldsCount = () => {
    return ignoredFields.size;
  };

  const toggleRowExpansion = (index: number) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(index)) {
      newExpanded.delete(index);
    } else {
      newExpanded.add(index);
    }
    setExpandedRows(newExpanded);
  };

  const getRecordType = (item: CompareDataItem, index: number) => {
    if (!item.current) return 'new';
    if (!item.incoming) return 'existing';
    
    // Check if all fields are identical (including ignored fields)
    const allFields = new Set<string>();
    if (item.current) Object.keys(item.current).forEach(key => allFields.add(key));
    if (item.incoming) Object.keys(item.incoming).forEach(key => allFields.add(key));
    
    let hasConflicts = false;
    for (const field of Array.from(allFields)) {
      if (!isFieldIdentical(index, field)) {
        hasConflicts = true;
        break;
      }
    }
    
    return hasConflicts ? 'conflict' : 'identical';
  };

  const getRecordTypeIcon = (type: string) => {
    const baseColor = isInAIAvatarLayout 
      ? (theme.palette.mode === 'light' ? '#000000' : '#ffffff')
      : undefined;
    
    switch (type) {
      case 'new':
        return <CheckCircleIcon sx={{ color: baseColor || '#2e7d32' }} />;
      case 'existing':
        return <InfoIcon sx={{ color: baseColor || '#1976d2' }} />;
      case 'conflict':
        return <WarningIcon sx={{ color: baseColor || '#ed6c02' }} />;
      default:
        return <CheckCircleIcon sx={{ color: baseColor || '#1976d2' }} />;
    }
  };

  const getRecordTypeColor = (type: string) => {
    if (isInAIAvatarLayout) {
      return theme.palette.mode === 'light' ? '#000000' : '#ffffff';
    }
    
    switch (type) {
      case 'new':
        return '#2e7d32'; // Green - same as filter
      case 'existing':
        return '#1976d2'; // Blue - same as filter
      case 'conflict':
        return '#ed6c02'; // Orange - same as filter
      default:
        return '#1976d2'; // Blue - same as filter
    }
  };

  const getRecordTypeLabel = (type: string) => {
    switch (type) {
      case 'new':
        return 'New';
      case 'existing':
        return 'Current';
      case 'conflict':
        return 'Conflicted';
      default:
        return 'Current';
    }
  };

  const renderObjectData = (obj: any, isEditable: boolean = false, onFieldEdit?: (key: string, value: any) => void) => {
    if (!obj) {
      return (
        <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
          No data
        </Typography>
      );
    }

    const entries = Object.entries(obj);
    if (entries.length === 0) {
      return (
        <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
          Empty object
        </Typography>
      );
    }

    return (
      <Box>
        <Box sx={{ maxHeight: 300, overflow: 'auto' }}>
          {entries.map(([key, value]) => (
            <Box key={key} sx={{ mb: 1, p: 1, backgroundColor: theme.palette.grey[50], borderRadius: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 'medium', mb: 0.5 }}>
                {key}:
              </Typography>
              {isEditable ? (
                <TextField
                  fullWidth
                  size="small"
                  value={typeof value === 'object' ? JSON.stringify(value, null, 2) : String(value)}
                  onChange={(e) => onFieldEdit?.(key, e.target.value)}
                  multiline
                  rows={2}
                  variant="outlined"
                  sx={{ 
                    '& .MuiOutlinedInput-root': {
                      fontSize: '0.875rem',
                      padding: '4px 8px'
                    }
                  }}
                />
              ) : (
                <Typography variant="body2" color="text.secondary" sx={{ 
                  wordBreak: 'break-word',
                  whiteSpace: 'pre-wrap'
                }}>
                  {typeof value === 'object' ? JSON.stringify(value, null, 2) : String(value)}
                </Typography>
              )}
            </Box>
          ))}
        </Box>
      </Box>
    );
  };


  // New function for synchronized side-by-side display with perfect alignment
  const renderObjectDataSync = (obj: any, isEditable: boolean, isIncoming: boolean, onFieldEdit?: (key: string, value: any) => void, rowIndex?: number) => {
    if (!obj) {
      return (
        <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', p: 2 }}>
          No data
        </Typography>
      );
    }

    // Get the other object for unified field ordering
    const otherObj = rowIndex !== undefined ? 
      (isIncoming ? editableData[rowIndex]?.current : editableData[rowIndex]?.incoming) : null;
    
    // Create unified field order
    const unifiedFieldOrder = createUnifiedFieldOrder(obj, otherObj);
    
    // If we're in conflicting fields mode, filter to show only conflicting fields
    let fieldsToShow = unifiedFieldOrder;
    if (showOnlyConflicts && rowIndex !== undefined) {
      const item = editableData[rowIndex];
      if (item && item._conflictingFields) {
        const conflictingFields = item._conflictingFields;
        fieldsToShow = unifiedFieldOrder.filter(key => key in conflictingFields);
      }
    }

    // Create entries in unified order
    const allEntries = fieldsToShow.map(key => [key, obj[key]]);

    return (
      <Grid container spacing={1.5}>
        {allEntries.map(([key, value]) => {
          const isIdentical = rowIndex !== undefined ? isFieldIdentical(rowIndex, key) : false;
          const isDifferent = rowIndex !== undefined ? isFieldDifferent(rowIndex, key) : false;
          const isMissing = rowIndex !== undefined ? isFieldMissingInIncoming(rowIndex, key) : false;
          const isCopied = rowIndex !== undefined ? copiedFields.has(`${rowIndex}-${key}`) : false;
          const isIgnored = rowIndex !== undefined ? isFieldIgnored(rowIndex, key) : false;
          
          // For incoming data, if field is missing, show placeholder
          const displayValue = isIncoming && isMissing ? null : value;
          
          return (
            <Grid item xs={12} key={key}>
              <Box sx={{ 
                p: 1.5, 
                backgroundColor: isIgnored ? theme.palette.grey[100] : 
                                isIncoming ? theme.palette.primary.light + '20' : theme.palette.grey[50], 
                borderRadius: 1.5,
                height: '120px', // Fixed height for perfect alignment
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'flex-start',
                border: isIdentical ? `2px solid ${theme.palette.success.main}` : 
                        isDifferent ? `2px solid ${theme.palette.warning.main}` :
                        isMissing ? `2px solid ${theme.palette.info.main}` : 'none',
                position: 'relative',
                opacity: (isMissing && isIncoming) || isIgnored ? 0.7 : 1,
                boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
              }}>
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'space-between',
                height: '24px', 
                marginBottom: '8px'
              }}>
                <Typography variant="body2" sx={{ 
                  fontWeight: 'medium',
                  color: isIgnored ? theme.palette.text.disabled : 'inherit',
                  textDecoration: isIgnored ? 'line-through' : 'none'
                }}>
                  {key}: {isIgnored && '(Ignored)'}
                </Typography>
                {!isIncoming && rowIndex !== undefined && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    {isIdentical && (
                      <CheckIcon color="success" sx={{ fontSize: 16 }} />
                    )}
                    {isIgnored && (
                      <BlockIcon color="disabled" sx={{ fontSize: 16 }} />
                    )}
                    {(isDifferent || isMissing) && !isIgnored && (
                      <Tooltip title={isMissing ? "Add to incoming data" : "Copy to incoming data"}>
                        <IconButton
                          size="small"
                          onClick={() => handleCopyField(rowIndex, key)}
                          sx={{ 
                            p: 0.5,
                            color: isCopied ? theme.palette.success.main : 
                                   isMissing ? theme.palette.info.main : theme.palette.primary.main
                          }}
                        >
                          {isCopied ? <CheckIcon sx={{ fontSize: 16 }} /> : <CopyIcon sx={{ fontSize: 16 }} />}
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                )}
                {isIncoming && rowIndex !== undefined && (isDifferent || isMissing) && !isIgnored && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Tooltip title="Ignore incoming value (use current value)">
                      <IconButton
                        size="small"
                        onClick={() => handleIgnoreField(rowIndex, key)}
                        sx={{ 
                          p: 0.5,
                          color: theme.palette.error.main,
                          '&:hover': {
                            backgroundColor: theme.palette.error.light + '20'
                          }
                        }}
                      >
                        <BlockIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Tooltip>
                  </Box>
                )}
              </Box>
              <Box sx={{ 
                flex: 1,
                display: 'flex', 
                alignItems: 'flex-start',
                height: '80px',
                overflow: 'hidden'
              }}>
                {isEditable ? (
                  <TextField
                    fullWidth
                    size="small"
                    value={isIgnored ? 'Ignored - using current value' : 
                           displayValue !== null ? (typeof displayValue === 'object' ? JSON.stringify(displayValue, null, 2) : String(displayValue)) : ''}
                    onChange={(e) => onFieldEdit?.(key, e.target.value)}
                    multiline
                    rows={3}
                    variant="outlined"
                    placeholder={isIgnored ? "Field ignored - using current value" : 
                                isMissing ? "Field not present - click copy icon to add" : ""}
                    disabled={isIgnored}
                    sx={{ 
                      '& .MuiOutlinedInput-root': {
                        fontSize: '0.875rem',
                        padding: '4px 8px',
                        opacity: isIgnored ? 0.6 : 1
                      }
                    }}
                  />
                ) : (
                  <Typography variant="body2" color={isIgnored ? "text.disabled" : "text.secondary"} sx={{ 
                    wordBreak: 'break-word',
                    whiteSpace: 'pre-wrap',
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    alignItems: 'flex-start',
                    padding: '8px 0',
                    overflow: 'auto',
                    textDecoration: isIgnored ? 'line-through' : 'none'
                  }}>
                    {isIgnored ? 'Ignored - using current value' : 
                     displayValue !== null ? (typeof displayValue === 'object' ? JSON.stringify(displayValue, null, 2) : String(displayValue)) : 'Field not present'}
                  </Typography>
                )}
              </Box>
              </Box>
            </Grid>
          );
        })}
      </Grid>
    );
  };

  const renderFieldComparison = (current: any, incoming: any) => {
    if (!current && !incoming) return null;
    if (!current) return <Chip label="New Field" color="success" size="small" />;
    if (!incoming) return <Chip label="Removed Field" color="error" size="small" />;
    
    const comparison = compareFieldValues('field', current, incoming);
    if (comparison.isDifferent) {
      return <Chip label="Modified" color="warning" size="small" />;
    }
    
    return <Chip label="Same" color="info" size="small" />;
  };

  return (
    <Box>
      <TableContainer 
        component={Paper} 
        elevation={0}
        sx={{
          borderRadius: 1.5,
          border: '1px solid rgba(15, 40, 76, 0.08)',
          boxShadow: '0 2px 8px rgba(15, 40, 76, 0.05)',
        }}
      >
        <Table size="small">
          <TableHead>
            <TableRow sx={{ 
              backgroundColor: isInAIAvatarLayout
                ? (theme.palette.mode === 'light' ? '#000000' : '#ffffff')
                : '#0F2A52'
            }}>
              <TableCell sx={{ 
                fontWeight: 600, 
                color: isInAIAvatarLayout
                  ? (theme.palette.mode === 'light' ? '#ffffff' : '#000000')
                  : 'white',
                fontSize: '0.875rem'
              }}>#</TableCell>
              <TableCell sx={{ 
                fontWeight: 600, 
                color: isInAIAvatarLayout
                  ? (theme.palette.mode === 'light' ? '#ffffff' : '#000000')
                  : 'white',
                fontSize: '0.875rem'
              }}>Type</TableCell>
              <TableCell sx={{ 
                fontWeight: 600, 
                color: isInAIAvatarLayout
                  ? (theme.palette.mode === 'light' ? '#ffffff' : '#000000')
                  : 'white',
                fontSize: '0.875rem'
              }}>Current Data</TableCell>
              <TableCell sx={{ 
                fontWeight: 600, 
                color: isInAIAvatarLayout
                  ? (theme.palette.mode === 'light' ? '#ffffff' : '#000000')
                  : 'white',
                fontSize: '0.875rem'
              }}>
                Incoming Data
                {getIgnoredFieldsCount() > 0 && (
                  <Chip 
                    label={`${getIgnoredFieldsCount()} ignored`}
                    size="small"
                    sx={{ 
                      ml: 1,
                      backgroundColor: 'rgba(255, 255, 255, 0.2)',
                      color: 'white',
                      fontSize: '0.7rem',
                      height: '20px'
                    }}
                  />
                )}
              </TableCell>
              <TableCell sx={{ 
                fontWeight: 600, 
                color: isInAIAvatarLayout
                  ? (theme.palette.mode === 'light' ? '#ffffff' : '#000000')
                  : 'white',
                fontSize: '0.875rem'
              }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {editableData.map((item, index) => {
              const recordType = getRecordType(item, index);
              const isExpanded = expandedRows.has(index);
              
              return (
                <React.Fragment key={index}>
                  <TableRow hover>
                    <TableCell>{index + 1}</TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {getRecordTypeIcon(recordType)}
                        <Chip
                          label={getRecordTypeLabel(recordType)}
                          size="small"
                          sx={{
                            backgroundColor: getRecordTypeColor(recordType) + '20', // 20% opacity
                            color: getRecordTypeColor(recordType),
                            border: `1px solid ${getRecordTypeColor(recordType)}`,
                            fontWeight: 500,
                            '& .MuiChip-label': {
                              color: getRecordTypeColor(recordType),
                              fontWeight: 500
                            }
                          }}
                        />
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {item.current ? `${Object.keys(item.current).length} fields` : 'N/A'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {item.incoming ? `${Object.keys(item.incoming).length} fields` : 'N/A'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Tooltip title={isExpanded ? 'Collapse' : 'Expand'}>
                        <IconButton
                          size="small"
                          onClick={() => toggleRowExpansion(index)}
                        >
                          {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                  
                  <TableRow>
                    <TableCell colSpan={5} sx={{ p: 0, border: 0 }}>
                      <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                        <Box sx={{ p: 2, backgroundColor: 'rgba(15, 42, 82, 0.02)' }}>
                          {/* Fixed Headers */}
                          <Box sx={{ 
                            display: 'flex', 
                            position: 'sticky', 
                            top: 0, 
                            zIndex: 1, 
                            backgroundColor: 'background.paper',
                            borderBottom: `2px solid ${isInAIAvatarLayout ? (theme.palette.mode === 'light' ? '#000000' : '#ffffff') : '#0F2A52'}`,
                            mb: 1,
                            borderRadius: 1,
                            overflow: 'hidden'
                          }}>
                            <Box sx={{ 
                              flex: 1, 
                              p: 1, 
                              fontWeight: 600, 
                              textAlign: 'center',
                              backgroundColor: isInAIAvatarLayout ? (theme.palette.mode === 'light' ? '#000000' : '#ffffff') : '#0F2A52',
                              color: isInAIAvatarLayout ? (theme.palette.mode === 'light' ? '#ffffff' : '#000000') : 'white',
                              fontSize: '0.875rem'
                            }}>
                              Current Data
                            </Box>
                            <Box sx={{ 
                              flex: 1, 
                              p: 1, 
                              fontWeight: 600, 
                              textAlign: 'center',
                              backgroundColor: isInAIAvatarLayout ? (theme.palette.mode === 'light' ? '#000000' : '#ffffff') : '#0F2A52',
                              color: isInAIAvatarLayout ? (theme.palette.mode === 'light' ? '#ffffff' : '#000000') : 'white',
                              fontSize: '0.875rem'
                            }}>
                              Incoming Data (Editable)
                            </Box>
                          </Box>
                          
                          {/* Side-by-side content with single scroll */}
                          <Box sx={{ 
                            maxHeight: 400, 
                            border: `1px solid ${theme.palette.divider}`,
                            borderRadius: 1,
                            overflow: 'auto'
                          }}>
                            {/* Single scroll container with synchronized fields */}
                            <Box sx={{ 
                              display: 'grid', 
                              gridTemplateColumns: '1fr 1fr',
                              gap: 0,
                              minHeight: '100%',
                              alignItems: 'start'
                            }}>
                              {/* Current Data - Left column */}
                              <Box sx={{ 
                                borderRight: `1px solid ${theme.palette.divider}`,
                                p: 1,
                                overflow: 'hidden'
                              }}>
                                {renderObjectDataSync(item.current, false, false, undefined, index)}
                              </Box>
                              {/* Incoming Data - Right column */}
                              <Box sx={{ 
                                p: 1,
                                overflow: 'hidden'
                              }}>
                                {renderObjectDataSync(item.incoming, true, true, (field, value) => handleFieldEdit(index, field, value), index)}
                              </Box>
                            </Box>
                          </Box>
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Ignored Fields Summary and Reset Button */}
      {getIgnoredFieldsCount() > 0 && (
        <Box sx={{ 
          mt: 2, 
          p: 2, 
          backgroundColor: theme.palette.grey[50], 
          borderRadius: 1,
          border: `1px solid ${theme.palette.grey[300]}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <BlockIcon color="disabled" />
            <Typography variant="body2" color="text.secondary">
              {getIgnoredFieldsCount()} field(s) ignored - using current values instead of incoming values
            </Typography>
          </Box>
          <Button
            size="small"
            variant="outlined"
            onClick={resetIgnoredFields}
            sx={{
              borderColor: theme.palette.grey[400],
              color: theme.palette.text.secondary,
              '&:hover': {
                borderColor: theme.palette.grey[600],
                backgroundColor: theme.palette.grey[100]
              }
            }}
          >
            Reset Ignored Fields
          </Button>
        </Box>
      )}

    </Box>
  );
};

export default CompareDataTable;
